import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterParamLoaderService } from '@app/core';
import { BehaviorSubject } from 'rxjs';
import {
  BookDetailResponse,
  Content,
  LivroSumarioProjeto,
} from '../../pages/educational-materials-details/book.details.interface';
import { TypeSummary } from '../../utils';

@Component({
  selector: 'app-element-summary',
  templateUrl: './element-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ElementSummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _routerParamLoaderService = inject(RouterParamLoaderService);
  private readonly _router = inject(Router);

  @Input() public showModal = false;
  @Input() public bookContent: BookDetailResponse;
  @Output() public bookSummaryProject = new EventEmitter<LivroSumarioProjeto[]>();
  @Output() public title = new EventEmitter<string>();
  @Output() public page = new EventEmitter<number>();

  public subjectActive$ = new BehaviorSubject<number>(0);
  public chapterActive$ = new BehaviorSubject<number>(0);
  public subjectActive = 0;

  private readonly _expressao = /-|[0-9]/g;

  public ngOnInit(): void {
    if (!this.showModal) {
      this.treatUrl();
    }
  }

  public ngOnDestroy(): void {
    this.subjectActive$.complete();
    this.chapterActive$.complete();
  }

  public ngAfterViewInit(): void {
    if (!this.showModal) {
      this.setDefaultBookSummaryProject(this.subjectActive$.value, this.chapterActive$.value);
    }
  }

  public tabStyle(subject: Content, tab: any): any {
    if (tab.active) {
      return { color: subject.color, 'background-color': '#fff' };
    }
    return { 'background-color': subject.color, color: this.calcTextColor(subject.color ?? '#fff') };
  }

  public setBookSummaryProject(
    color: string,
    bookSummaryProject: Content,
    index?: number,
    page?: number,
    event?: PointerEvent,
  ): void {
    event?.stopPropagation();
    if (!this.showModal) {
      if (!!bookSummaryProject?.livro_sumario_projetos?.length && !this.bookContent.has_lock) {
        this.title.emit(bookSummaryProject?.title);
        this.bookSummaryProject.emit(bookSummaryProject?.livro_sumario_projetos?.map((data) => ({ ...data, color })));
      }
      if (this.bookContent.formato_apresentacao === TypeSummary.List) {
        this.subjectActive$.next(index);
      }
      this.updateUrl(
        this.subjectActive$.value,
        this.bookContent.formato_apresentacao === TypeSummary.Tabs ? index : undefined,
      );
    } else if (!!page) {
      this.page.emit(page);
    }
  }

  public getIndexSubject(index: number): void {
    if (!this.showModal) {
      this.setDefaultBookSummaryProject(index);
      this.subjectActive$.next(index);
      if (this.bookContent.formato_apresentacao === TypeSummary.Tabs) {
        this.chapterActive$.next(0);
      }
    }
  }

  private setDefaultBookSummaryProject(subject: number, chapter?: number): void {
    if (this.bookContent.formato_apresentacao === TypeSummary.Tabs) {
      this.setBookSummaryProject(
        this.bookContent.contents[subject]?.color,
        this.bookContent.contents[subject]?.contents[chapter ?? 0],
        chapter ?? 0,
      );
      this.updateUrl(subject, chapter ?? 0);
    } else {
      this.setBookSummaryProject(this.bookContent.contents[subject]?.color, this.bookContent.contents[subject]);
      this.updateUrl(subject);
    }
  }

  private treatUrl(): void {
    if (!!this.bookContent.contents.length) {
      const slug = this._activatedRoute.snapshot.paramMap.get('slug');
      const subject = this._activatedRoute.snapshot.paramMap.get('subject');
      if (slug !== this.bookContent?.slug) {
        const subject_slug = subject.replace(this._expressao, '');
        this.treatSubject(subject_slug, true);
      } else {
        this.treatSubject(subject);
      }

      const chapter = this._activatedRoute.snapshot.paramMap.get('chapter');
      if (slug !== this.bookContent?.slug) {
        const chapter_slug = chapter.replace(this._expressao, '');
        this.treatChapter(this.subjectActive$.value, chapter_slug, true);
      } else {
        this.treatChapter(this.subjectActive$.value, chapter);
      }
    }
  }

  private treatSubject(subject: string, useRegex = false): boolean {
    if (subject) {
      const index = this.bookContent.contents?.findIndex(
        (sub) => (useRegex ? sub?.slug.replace(this._expressao, '') : sub?.slug) === subject,
      );
      if (index !== -1) {
        this.subjectActive$.next(index);
        this.subjectActive = index;
        return true;
      }
    }
    this.subjectActive$.next(0);
    this.subjectActive = 0;
    this.updateUrl(0, 0);
    return false;
  }

  private treatChapter(subject: number, chapter: string, useRegex = false): boolean {
    if (chapter) {
      if (this.bookContent.formato_apresentacao === TypeSummary.Tabs) {
        const index = this.bookContent.contents[subject].contents.findIndex(
          (chap) => (useRegex ? chap?.slug.replace(this._expressao, '') : chap?.slug) === chapter,
        );
        if (index !== -1) {
          this.chapterActive$.next(index);
          this.updateUrl(subject, index);
          return true;
        }
        this.updateUrl(subject, 0);
        this.chapterActive$.next(0);
        return false;
      }
      this.chapterActive$.next(0);
      this.updateUrl(subject);
      return false;
    }
    if (this.bookContent.formato_apresentacao === TypeSummary.Tabs) {
      this.chapterActive$.next(0);
      this.updateUrl(subject, 0);
    }
    return false;
  }

  private calcTextColor(hex: string): string {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    let rgb: any = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    rgb = rgb
      ? { red: parseInt(rgb[1], 16), green: parseInt(rgb[2], 16), blue: parseInt(rgb[3], 16) }
      : { red: 0, green: 0, blue: 0 };
    return (
      '#' +
      (Math.round((parseInt(rgb.red) * 299 + parseInt(rgb.green) * 587 + parseInt(rgb.blue) * 114) / 1000) > 150
        ? '555'
        : 'FFF')
    );
  }

  private updateUrl(subject?: number, chapter?: number): void {
    const url = this._router.url.split('/');
    this._routerParamLoaderService.updateUrl(
      {
        subject: subject >= 0 ? this.bookContent.contents[subject]?.slug : '',
        chapter: chapter >= 0 ? this.bookContent.contents[subject]?.contents[chapter]?.slug : '',
      },
      url[1] + '/' + this.bookContent.slug,
    );
  }
}
